import { disposeMesh } from 'src/utils/object3DHelpers';
import { ComponentInteractionType, Inputs, SceneComponent } from './types';

const WIDTH = 1;
const HEIGHT = 1;
const DEPTH = 1;
const DEFAULT_INPUTS = {
  colorDefault: '#ea475c',
  colorHover: '#ffffff',
  colorClick: '#ea475c',
  opacityDefault: 0.2,
  opacityHover: 0.4,
  opacityClick: 0.5,
  edgesOpacityDefault: 0.5,
  edgesOpacityHover: 0.7,
  edgesOpacityClick: 0.8
};

export class PolygonComponent extends SceneComponent {
  private root: any = null;

  private pivot: any = null;

  private polygon: any = null;

  private edges: any = null;

  private currentColor = '#ea475c';

  private currentOpacity = 0.2;

  private currentEdgesOpacity = 0.5;

  private clicked = false;

  inputs: Inputs = {
    ...DEFAULT_INPUTS
  };

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false
  };

  onInit() {
    const THREE = this.context.three;

    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    // setting the collider to add the click event
    this.outputs.collider = this.root;
    this.outputs.objectRoot = this.root;
    this.root.add(this.pivot);
    this.currentColor = this.inputs.colorDefault;
    this.currentOpacity = this.inputs.opacityDefault;
    this.currentEdgesOpacity = this.inputs.edgesOpacityDefault;

    this.makePolygon();
  }

  onDestroy() {
    if (this.polygon) {
      disposeMesh(this.polygon);
      this.polygon = null;
    }
    if (this.edges) {
      disposeMesh(this.edges);
      this.edges = null;
    }
  }

  // Called back when any inputs properties change like localPosition or localRotation
  onInputsUpdated() {
    this.onInit();
  }

  // used to fire it from outside event handler,
  // as internal handler doesn't work if outside handler is defined
  handleHover(event: any) {
    if (!this.clicked) {
      if (event.hover) {
        this.currentColor = this.inputs.colorHover;
        this.currentOpacity = this.inputs.opacityHover;
        this.currentEdgesOpacity = this.inputs.edgesOpacityHover;
      } else {
        this.currentColor = this.inputs.colorDefault;
        this.currentOpacity = this.inputs.opacityDefault;
        this.currentEdgesOpacity = this.inputs.edgesOpacityDefault;
      }
      if (this.polygon) {
        (this.polygon.material as any).color.setStyle(this.currentColor);
        (this.polygon.material as any).opacity = this.currentOpacity;
      }

      if (this.edges) {
        (this.edges.material as any).color.setStyle(this.currentColor);
        (this.edges.material as any).opacity = this.currentEdgesOpacity;
        this.edges.visible = this.currentEdgesOpacity !== 0;
      }
    }
  }

  handleClick() {
    if (!this.clicked) {
      this.currentColor = this.inputs.colorClick;
      this.currentOpacity = this.inputs.opacityClick;
      this.currentEdgesOpacity = this.inputs.edgesOpacityClick;
      this.clicked = true;
    } else {
      this.currentColor = this.inputs.colorDefault;
      this.currentOpacity = this.inputs.opacityDefault;
      this.currentEdgesOpacity = this.inputs.edgesOpacityDefault;
      this.clicked = false;
    }
    if (this.polygon) {
      (this.polygon.material as any).color.setStyle(this.currentColor);
      (this.polygon.material as any).opacity = this.currentOpacity;
    }

    if (this.edges) {
      (this.edges.material as any).color.setStyle(this.currentColor);
      (this.edges.material as any).opacity = this.currentEdgesOpacity;
      this.edges.visible = this.currentEdgesOpacity !== 0;
    }
    setTimeout(() => {
      this.clicked = false;
    }, 1000);
  }

  setSelection(color: string) {
    this.inputs.colorDefault = color || this.inputs.colorClick;
    this.inputs.opacityDefault = color ? this.inputs.opacityDefault : this.inputs.opacityClick;
    if (this.polygon) {
      (this.polygon.material as any).color.setStyle(this.inputs.colorDefault);
    }
    if (this.edges) {
      (this.edges.material as any).opacity = this.inputs.opacityDefault;
      this.edges.visible = this.inputs.opacityDefault !== 0;
    }
  }

  resetToDefaultColor() {
    this.inputs.colorDefault = DEFAULT_INPUTS.colorClick;
    this.inputs.opacityClick = DEFAULT_INPUTS.opacityClick;
    (this.polygon.material as any).color.setStyle(this.inputs.colorDefault);
    (this.edges.material as any).opacity = this.inputs.opacityDefault;
    this.edges.visible = this.inputs.opacityDefault !== 0;
  }

  hide() {
    (this.polygon.material as any).opacity = 0;
    (this.edges.material as any).opacity = 0;
    this.edges.visible = false;
  }

  private makePolygon() {
    const THREE = this.context.three;

    if (this.polygon) {
      disposeMesh(this.polygon);
      this.polygon = null;
    }
    if (this.edges) {
      disposeMesh(this.edges);
      this.edges = null;
    }

    const boxGeometry: any = new THREE.BoxGeometry(WIDTH, HEIGHT, DEPTH);

    const boxMaterial: any = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: this.currentOpacity,
      side: THREE.DoubleSide,
      // blending: THREE.AdditiveBlending,
      blending: THREE.NormalBlending,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -5
    });
    boxMaterial.color.setStyle(this.currentColor);
    this.polygon = new THREE.Mesh(boxGeometry, boxMaterial);
    this.root?.add(this.polygon);

    const edgesGeometry = new THREE.EdgesGeometry(boxGeometry);
    const edgesMaterial = new THREE.LineBasicMaterial({
      transparent: true,
      opacity: this.currentEdgesOpacity
    });
    edgesMaterial.color.setStyle(this.currentColor);
    this.edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
    this.edges.visible = this.currentEdgesOpacity !== 0;
    this.root?.add(this.edges);
  }
}

export const polygonComponentType = 'treedis.polygonComponent';
export const makePolygonComponent = () => new PolygonComponent();
